import ReactDOM from "react-dom/client";
import App from "./App";
import { msalInstance } from "./authConfig";
import { MsalProvider } from "@azure/msal-react";
import "./theme/dx-material-TCP-theme-v1.css";
import "./styles/index.css";
import "./styles/library.css";
import "./styles/print.css";
import { Provider } from "react-redux";
import store from "./redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import config from "devextreme/core/config";
import { DX_LICENSE_KEY } from "./helper/devextreme-license";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { ENVIRONMENT, SENTRY_DSN } from "./constants/constants";
import { getConfig } from "./config";
import { DomainConfigProvider } from "./components/DomainConfigProvider";

config({ licenseKey: DX_LICENSE_KEY });

declare global {
  interface Window {
    __APP_CONFIG__?: {
      ENV: string;
      APP_NAME: string;
      CLIENT_ID: string;
      TENANT: string;
      SENTRY_DSN: string;
      API_ID: string;
      STOCK_PRICE_TTL?: string;
      SERVER_TIME?: string;
      BACKEND_URL: string;
      FRONTEND_URL: string;
    };
  }
}

getConfig();
const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 0, staleTime: 0 } },
});

const isProd = ENVIRONMENT.toLowerCase().includes("prod");
const replaySessionSampleRate = isProd ? 0.1 : 0;
const replaysOnErrorSampleRate = isProd ? 1.0 : 0;
const tracesSampleRate = isProd ? 0.1 : 1.0;

Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: false,
    }),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: tracesSampleRate,
  replaysSessionSampleRate: replaySessionSampleRate,
  replaysOnErrorSampleRate: replaysOnErrorSampleRate,
  environment: ENVIRONMENT,
  release: APP_VERSION,
  beforeSendTransaction(transaction) {
    const transactionName = transaction.transaction?.toLowerCase() || "";
    const url = transaction.contexts?.trace?.data?.url?.toLowerCase() || "";

    const transactionsToFilter = [
      "/assets/",
      "/public/",
      "/static/",
      "favicon.ico",
      "manifest.json",
      "robots.txt",
      "/meta.json",
      "meta.json",
      "*path:nonfile",
      "(*path:nonfile)",
      "wp-includes",
      "wlwmanifest.xml",
      "xmlrpc.php",
      "wp-admin",
      "wp-content",
    ];

    if (transactionsToFilter.some((term) => transactionName.includes(term) || url.includes(term))) {
      return null;
    }

    return transaction;
  },

  beforeSend(event, hint) {
    const error = hint.originalException;

    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      if (
        message.includes("loading chunk") ||
        message.includes("loading css chunk") ||
        (message.includes("failed to fetch") && message.includes("/assets/")) ||
        message.includes("script error")
      ) {
        return null;
      }
    }

    if (event.request?.url) {
      const url = event.request.url.toLowerCase();
      if (url.includes("/assets/") || url.includes("/public/") || url.includes("/static/")) {
        return null;
      }
    }

    return event;
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <MsalProvider instance={msalInstance}>
    <QueryClientProvider client={queryClient}>
      <ToastContainer autoClose={2000} limit={4} draggable pauseOnHover />
      <Provider store={store}>
        <DomainConfigProvider>
          <App />
          <ReactQueryDevtools initialIsOpen={false} position={"right"} />
        </DomainConfigProvider>
      </Provider>
    </QueryClientProvider>
  </MsalProvider>
);
